import Court from "./Court.tsx";
import halfcourtIcon from "./icons/halfcourt.svg";
import fullcourtIcon from "./icons/fullcourt.svg";
import { Line, Player, Cone, TextBox, Shade } from "./DialogPlayBuilder.tsx";
import React, { Fragment } from "react";

export const courtDimensions = {
    halfcourt: { width: 542, height: 455 },
    fullcourt: { width: 910, height: 550 },
};

const NAMED_COLORS: Record<string, string> = {
    black: "#000000",
    blue: "#0000FF",
    green: "#008000",
    grey: "#808080",
    gray: "#808080",
    maroon: "#800000",
    navy: "#000080",
    orange: "#FFA500",
    purple: "#800080",
    red: "#FF0000",
    white: "#FFFFFF",
    yellow: "#FFFF00",
};

type GradientPalette = {
    highlight: string;
    mid: string;
    shadow: string;
    outline: string;
    limbPrimary: string;
    limbSecondary: string;
};

const normalizeColor = (color?: string | null): string | null => {
    if (!color) return null;
    const trimmed = color.trim();
    if (!trimmed) return null;
    const lower = trimmed.toLowerCase();
    if (NAMED_COLORS[lower]) return NAMED_COLORS[lower];
    const hexMatch = lower.match(/^#([\da-f]{3}|[\da-f]{6})$/i);
    if (!hexMatch) return null;
    let hex = hexMatch[1];
    if (hex.length === 3) {
        hex = hex.split("").map((ch) => ch + ch).join("");
    }
    return `#${hex.toUpperCase()}`;
};

const toRgba = (color: string | undefined, alpha: number) => {
    const normalized = normalizeColor(color) ?? "#38BDF8";
    const hex = normalized.slice(1);
    const num = parseInt(hex, 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    const a = Math.max(0, Math.min(1, alpha));
    return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const adjustColor = (color: string, amount: number): string => {
    const normalized = normalizeColor(color);
    if (!normalized) return color;
    const hex = normalized.slice(1);
    const num = parseInt(hex, 16);
    const clamp = (value: number) => Math.max(0, Math.min(255, Math.round(value)));
    const adjustChannel = (channel: number) =>
        amount >= 0 ? channel + (255 - channel) * amount : channel * (1 + amount);
    let r = (num >> 16) & 255;
    let g = (num >> 8) & 255;
    let b = num & 255;
    r = clamp(adjustChannel(r));
    g = clamp(adjustChannel(g));
    b = clamp(adjustChannel(b));
    const combined = (r << 16) | (g << 8) | b;
    return `#${combined.toString(16).padStart(6, "0").toUpperCase()}`;
};

const getGradientPalette = (color: string): GradientPalette => {
    const normalized = normalizeColor(color) ?? "#4A4A4A";
    return {
        highlight: adjustColor(normalized, 0.35),
        mid: normalized,
        shadow: adjustColor(normalized, -0.45),
        outline: adjustColor(normalized, -0.6),
        limbPrimary: adjustColor(normalized, -0.25),
        limbSecondary: adjustColor(normalized, -0.4),
    };
};


// Centralized configuration for frame rendering across the application.
export const FRAME_RENDER_CONFIG = {
    player: {
        ballRadius: 17,
        ballStrokeWidth: 2.7,
        getFontSize: (charCount: number) => {
            if (charCount === 1) return 24; // size of the caracters when 1 caracter
            if (charCount === 2) return 20; // size of the caracters when 2 caracters
            return 13; // size of the caracters when 3 caracters
        }
    },
    line: {
        strokeWidth: 2.5,      // thickness of the lines
        markerSize: 15,       // size of the markers
        offsets: {
            baseFromOffsetBarStraight: 15,
            baseFromOffsetOthers: 19,
            baseEndOffsetBarStraight: 0,
            baseEndOffsetOthers: 18,
            zigzagAdjustment: 2,
            afterPassOrHandoff: 15  // offset for lines that come after a pass or handoff to prevent overlapping with the circle
        }
    },
    cone: {
        scale: 1.5
    }
} as const;

// Configuration for PDF rendering with larger values for better visibility
export const FRAME_RENDER_CONFIG_FOR_PDF = {
    player: {
        ballRadius: 21,
        ballStrokeWidth: 3.5,
        getFontSize: (charCount: number) => {
            if (charCount === 1) return 30; // size of the caracters when 1 caracter
            if (charCount === 2) return 27; // size of the caracters when 2 caracters
            return 18; // size of the caracters when 3 caracters
        }
    },
    line: {
        strokeWidth: 3.2,      // thickness of the lines
        markerSize: 15,       // size of the markers
        offsets: {
            baseFromOffsetBarStraight: 19,
            baseFromOffsetOthers: 24,
            baseEndOffsetBarStraight: 0,
            baseEndOffsetOthers: 25,
            zigzagAdjustment: 2.7,
            afterPassOrHandoff: 20  // offset for lines that come after a pass or handoff to prevent overlapping with the circle
        }
    },
    cone: {
        scale: 1.95
    }
} as const;

export const approximateBezierLength = (from: { x: number; y: number }, control: { x: number; y: number }, to: { x: number; y: number }) => {
    const steps = 10;
    let length = 0, prev = from;
    for (let i = 1; i <= steps; i++) {
        const t = i / steps;
        const point = {
            x: (1 - t) ** 2 * from.x + 2 * (1 - t) * t * control.x + t ** 2 * to.x,
            y: (1 - t) ** 2 * from.y + 2 * (1 - t) * t * control.y + t ** 2 * to.y,
        };
        length += Math.hypot(point.x - prev.x, point.y - prev.y);
        prev = point;
    }
    return length;
};

export const createZigzagPath = (
    from: { x: number; y: number },
    control: { x: number; y: number },
    to: { x: number; y: number },
    amplitude = 3,
    oscillationsPer100px = 16,
    finalSegmentLength = 16,
    includeFinalSegment = true
) => {
    const length = approximateBezierLength(from, control, to);
    const totalOsc = Math.max(2, Math.round((length / 100) * oscillationsPer100px));
    const steps = 90, dt = 1 / steps;
    let totalLength = 0, prev = from;
    const cumulative = [0];
    for (let i = 1; i <= steps; i++) {
        const t = i * dt;
        const point = {
            x: (1 - t) ** 2 * from.x + 2 * (1 - t) * t * control.x + t ** 2 * to.x,
            y: (1 - t) ** 2 * from.y + 2 * (1 - t) * t * control.y + t ** 2 * to.y,
        };
        totalLength += Math.hypot(point.x - prev.x, point.y - prev.y);
        cumulative.push(totalLength);
        prev = point;
    }
    const path = [`M ${from.x} ${from.y}`];
    for (let i = 1; i <= steps; i++) {
        const s = (i / steps) * totalLength;
        const j = cumulative.findIndex((cl) => cl >= s) || 1;
        const t0 = (j - 1) * dt, t1 = j * dt, s0 = cumulative[j - 1], s1 = cumulative[j];
        const t = t0 + ((s - s0) / (s1 - s0)) * (t1 - t0);
        const point = {
            x: (1 - t) ** 2 * from.x + 2 * (1 - t) * t * control.x + t ** 2 * to.x,
            y: (1 - t) ** 2 * from.y + 2 * (1 - t) * t * control.y + t ** 2 * to.y,
        };
        const dxdt = 2 * (1 - t) * (control.x - from.x) + 2 * t * (to.x - control.x),
            dydt = 2 * (1 - t) * (control.y - from.y) + 2 * t * (to.y - control.y),
            len = Math.hypot(dxdt, dydt),
            nx = -dydt / len, ny = dxdt / len,
            frequency = (totalOsc * Math.PI) / totalLength,
            offset = amplitude * Math.sin(s * frequency);
        path.push(`L ${point.x + offset * nx} ${point.y + offset * ny}`);
    }
    if (includeFinalSegment) {
        const theta = Math.atan2(to.y - control.y, to.x - control.x);
        path.push(`L ${to.x + finalSegmentLength * Math.cos(theta)} ${to.y + finalSegmentLength * Math.sin(theta)}`);
    } else {
        path.push(`L ${to.x} ${to.y}`);
    }
    return path.join(' ');
};

export const SvgDefs: React.FC<{ frameId?: number; lineId: number; color: string; playNid?: number; markerSize?: number; lineType?: string }> = ({ frameId, lineId, color, playNid, markerSize = 25, lineType }) => {
    // Build the prefix based on provided props
    const prefixParts: string[] = [];
    if (playNid !== undefined) {
        prefixParts.push(String(playNid));
    }
    if (frameId !== undefined) {
        prefixParts.push(String(frameId));
    }
    const prefix = prefixParts.join('-');

    // For handoff lines, use the same white transparent color as the line
    const arrowColor = lineType === 'handoff' ? 'rgba(255, 255, 255, 0.9)' : color;

    // Clean colors for use in IDs (same as DialogPlayBuilder)
    const cleanedArrowColor = arrowColor.replace(/[^a-z0-9]/gi, '_');
    const cleanedColor = color.replace(/[^a-z0-9]/gi, '_');

    // Construct marker IDs with color to ensure updates when color changes
    const arrowheadId = prefix ? `arrowhead-${prefix}-${lineId}-${cleanedArrowColor}` : `arrowhead-${lineId}-${cleanedArrowColor}`;
    const barEndId = prefix ? `barEnd-${prefix}-${lineId}-${cleanedColor}` : `barEnd-${lineId}-${cleanedColor}`;

    return (
        <defs>
            <marker
                id={arrowheadId}
                markerWidth={markerSize}
                markerHeight={markerSize * 0.7}
                refX={markerSize * 0.75}
                refY={markerSize * 0.35}
                orient="auto"
                markerUnits="strokeWidth"
            >
                <polygon
                    points={`${markerSize * 0.9} ${markerSize * 0.35}, ${markerSize * 0.2} ${markerSize * 0.1}, ${markerSize * 0.4} ${markerSize * 0.35}, ${markerSize * 0.2} ${markerSize * 0.6}`}
                    fill={arrowColor}
                />
            </marker>
            <marker
                id={barEndId}
                markerWidth="1"
                markerHeight={markerSize * 1.0}
                refX="0.25"
                refY={(markerSize * 1.0) / 2}
                orient="auto"
                markerUnits="strokeWidth"
            >
                <rect x="0" y="0" width="1" height={markerSize * 1.0} fill={color} />
            </marker>
        </defs>
    );
};

export const getPlayerLinesInOrder = (lines: any[], playerId: number) =>
    lines.filter((l) => l.fromId === playerId).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

export const getOffsetPoint = (x1: number, y1: number, x2: number, y2: number, offset: number) => {
    const dx = x2 - x1, dy = y2 - y1, len = Math.hypot(dx, dy);
    return len ? { x: x1 + dx * (offset / len), y: y1 + dy * (offset / len) } : { x: x1, y: y1 };
};

export const generatePathD = (line: Line, adjustedFrom: { x: number; y: number }, adjustedTo: { x: number; y: number }, allLines: Line[], isForPDF: boolean = false, scale: number = 1.0, simplifyZigzag: boolean = false) => {
    // Check if this is a migrated FastDraw line - if so, skip offsets and use exact coordinates
    const isMigratedLine = line.uniqueId && line.uniqueId.startsWith('fastdraw-');

    if (isMigratedLine) {
        // For migrated lines, use exact coordinates without any offsets
        const start = { x: line.fromX, y: line.fromY };
        const end = { x: line.toX, y: line.toY };

        // Handle handoff lines - they must be straight lines
        if (line.type === 'handoff') {
            return `M ${start.x} ${start.y} L ${end.x} ${end.y}`;
        }

        // Handle double bezier lines
        if (line.midX !== undefined && line.midY !== undefined && line.controlX2 !== undefined && line.controlY2 !== undefined) {
            const mid = { x: line.midX, y: line.midY };
            if (line.type === 'zigzag' && !simplifyZigzag) {
                // First zigzag segment: start to mid, no final segment
                const firstSegment = createZigzagPath(start, { x: line.controlX, y: line.controlY }, mid, 3, 16, 16, false);
                // Second zigzag segment: mid to end, with final segment
                const secondSegment = createZigzagPath(mid, { x: line.controlX2, y: line.controlY2 }, end, 3, 16, 16, true).replace('M', '');
                return `${firstSegment} ${secondSegment}`;
            } else {
                // Non-zigzag duplicated lines use double Bézier (or simplified zigzag)
                return `M ${start.x} ${start.y} Q ${line.controlX} ${line.controlY} ${mid.x} ${mid.y} Q ${line.controlX2} ${line.controlY2} ${end.x} ${end.y}`;
            }
        }
        // Single-segment lines
        else if (line.type === 'zigzag' && !simplifyZigzag) {
            return createZigzagPath(start, { x: line.controlX, y: line.controlY }, end);
        }
        else {
            return `M ${start.x} ${start.y} Q ${line.controlX} ${line.controlY} ${end.x} ${end.y}`;
        }
    }

    // Get all lines for the player and sort them by order
    const playerLines = getPlayerLinesInOrder(allLines, line.fromId);
    const isFirstLine = playerLines.length > 0 && playerLines[0].id === line.id;

    // Find the line immediately before this one
    const lineIndex = playerLines.findIndex(l => l.id === line.id);
    const previousLine = lineIndex > 0 ? playerLines[lineIndex - 1] : null;
    const isPreviousLinePassOrHandoff = previousLine && (previousLine.type === 'dashed' || previousLine.type === 'handoff');

    // Find the last non-dashed, non-shot, non-handoff line to use its endpoint as the origin
    const lastNonDashedShotHandoffLine = playerLines
        .filter(l => l.id !== line.id && l.order < (line.order ?? 0) && l.type !== 'dashed' && l.type !== 'shot' && l.type !== 'handoff')
        .sort((a, b) => (b.order ?? 0) - (a.order ?? 0))[0];

    // If we found a previous non-dashed, non-shot, non-handoff line, use its endpoint
    if (lastNonDashedShotHandoffLine) {
        adjustedFrom = { x: lastNonDashedShotHandoffLine.toX, y: lastNonDashedShotHandoffLine.toY };
    }

    // Apply offset logic - use PDF config if specified
    const config = isForPDF ? FRAME_RENDER_CONFIG_FOR_PDF.line.offsets : FRAME_RENDER_CONFIG.line.offsets;
    let baseFromOffset: number;

    // If line comes after a pass or handoff, use special offset
    if (isPreviousLinePassOrHandoff) {
        baseFromOffset = config.afterPassOrHandoff;
    }
    // Otherwise use the existing logic
    else if ((line.type === 'bar' || line.type === 'straight') && (isFirstLine || lastNonDashedShotHandoffLine)) {
        baseFromOffset = config.baseFromOffsetBarStraight;
    }
    else if (isFirstLine || lastNonDashedShotHandoffLine) {
        baseFromOffset = config.baseFromOffsetOthers;
    }
    else {
        baseFromOffset = 0;
    }

    // Adjust for zigzag: subtract adjustment for zigzag, add adjustment for non-zigzag
    const fromOffset = baseFromOffset > 0
        ? (line.type === 'zigzag' ? baseFromOffset - config.zigzagAdjustment : baseFromOffset + config.zigzagAdjustment) * scale
        : 0;

    const start = fromOffset > 0
        ? getOffsetPoint(adjustedFrom.x, adjustedFrom.y, line.controlX, line.controlY, fromOffset)
        : { x: adjustedFrom.x, y: adjustedFrom.y };

    const baseEndOffset = (line.type === 'bar' || line.type === 'straight') ? config.baseEndOffsetBarStraight : config.baseEndOffsetOthers;
    const endOffset = baseEndOffset > 0
        ? (line.type === 'zigzag' ? baseEndOffset - config.zigzagAdjustment : baseEndOffset + config.zigzagAdjustment) * scale
        : 0;
    const end = getOffsetPoint(
        adjustedTo.x,
        adjustedTo.y,
        line.midX && line.controlX2 ? line.controlX2 : line.controlX,
        line.midY && line.controlY2 ? line.controlY2 : line.controlY,
        endOffset
    );

    // Handle handoff lines - they must be straight lines
    if (line.type === 'handoff') {
        return `M ${start.x} ${start.y} L ${end.x} ${end.y}`;
    }

    // Handle double bezier lines
    if (line.midX !== undefined && line.midY !== undefined && line.controlX2 !== undefined && line.controlY2 !== undefined) {
        const mid = { x: line.midX, y: line.midY };
        if (line.type === 'zigzag' && !simplifyZigzag) {
            // First zigzag segment: start to mid, no final segment
            const firstSegment = createZigzagPath(start, { x: line.controlX, y: line.controlY }, mid, 3, 16, 16, false);
            // Second zigzag segment: mid to end, with final segment
            const secondSegment = createZigzagPath(mid, { x: line.controlX2, y: line.controlY2 }, end, 3, 16, 16, true).replace('M', '');
            return `${firstSegment} ${secondSegment}`;
        } else {
            // Non-zigzag duplicated lines use double Bézier (or simplified zigzag)
            return `M ${start.x} ${start.y} Q ${line.controlX} ${line.controlY} ${mid.x} ${mid.y} Q ${line.controlX2} ${line.controlY2} ${end.x} ${end.y}`;
        }
    }
    // Single-segment lines
    else if (line.type === 'zigzag' && !simplifyZigzag) {
        return createZigzagPath(start, { x: line.controlX, y: line.controlY }, end);
    }
    else {
        return `M ${start.x} ${start.y} Q ${line.controlX} ${line.controlY} ${end.x} ${end.y}`;
    }
};


// Function to render a single player as an SVG group
// Function to render a single player as an SVG group
export const renderPlayerSVG = (
    player: Player,
    options: {
        fontSize?: number;
        ballRadius?: number;
        textFill?: string;
        ballStroke?: string;
        ballStrokeWidth?: number;
        isForPDF?: boolean;
        scale?: number;
        gradientIds?: {
            head: string;
            body: string;
            limb: string;
        };
        palette?: GradientPalette;
        filterId?: string;
        heldBallGradientId?: string;
        allow3DForPDF?: boolean;
    } = {}
) => {
    const displayNumber = player.isCoach ? "C" : player.isSpecialDefender ? String(player.number) : player.isDefender ? `x${player.number}` : String(player.number);

    // Calculate fontSize based on character count if not explicitly provided
    const charCount = displayNumber.length;
    const config = options.isForPDF ? FRAME_RENDER_CONFIG_FOR_PDF : FRAME_RENDER_CONFIG;
    const defaultFontSize = config.player.getFontSize(charCount);
    const {
        fontSize = defaultFontSize,
        ballRadius = config.player.ballRadius,
        textFill = player.color || (player.isDefender ? "red" : "black"),
        ballStroke = player.color || (player.isDefender ? "red" : "black"),
        ballStrokeWidth = config.player.ballStrokeWidth,
    } = options;

    const gradientIds = options.gradientIds;
    const palette = options.palette;
    const filterId = options.filterId;
    const heldBallGradientId = options.heldBallGradientId;

    // If rendering for PDF or palette information isn't provided, fallback to legacy simple rendering
    if ((options.isForPDF && !options.allow3DForPDF) || !gradientIds || !palette) {
        const textFillValue = player.isSpecialDefender ? "#FFFFFF" : textFill;
        const circleColor = player.color || (player.isDefender ? "red" : "black");
        const armRotation = player.armRotation || 0;
        const simpleArmLength = 14;
        const simpleArmHeight = 4;
        const simpleArmDistance = 19;
        const armYOffset = 2;
        return (
            <g key={player.id}>
                {player.hasBall && (
                    <circle
                        cx={player.x}
                        cy={player.y}
                        r={ballRadius}
                        stroke={ballStroke}
                        strokeWidth={ballStrokeWidth}
                        fill="none"
                    />
                )}
                {player.isSpecialDefender && (
                    <>
                        <circle
                            cx={player.x}
                            cy={player.y}
                            r={13}
                            fill={circleColor}
                            stroke={circleColor}
                            strokeWidth={2}
                        />
                        <g transform={`translate(${player.x}, ${player.y}) rotate(${armRotation})`}>
                            <g transform={`translate(${-simpleArmDistance}, ${armYOffset}) rotate(-30)`}>
                                <path
                                    d={`M -${simpleArmLength} 0 A ${simpleArmLength} ${simpleArmHeight} 0 0 1 ${simpleArmLength} 0`}
                                    fill="none"
                                    stroke={circleColor}
                                    strokeWidth={4}
                                    strokeLinecap="round"
                                />
                            </g>
                            <g transform={`translate(${simpleArmDistance}, ${armYOffset}) rotate(30)`}>
                                <path
                                    d={`M -${simpleArmLength} 0 A ${simpleArmLength} ${simpleArmHeight} 0 0 1 ${simpleArmLength} 0`}
                                    fill="none"
                                    stroke={circleColor}
                                    strokeWidth={4}
                                    strokeLinecap="round"
                                />
                            </g>
                        </g>
                    </>
                )}
                <text
                    x={player.x}
                    y={player.y}
                    textAnchor="middle"
                    alignmentBaseline="central"
                    fontSize={fontSize}
                    fontWeight="bold"
                    fill={textFillValue}
                    style={{ fontFamily: 'Arial, sans-serif' }}
                >
                    {displayNumber}
                </text>
            </g>
        );
    }

    const scaleFactor = options.scale ?? 0.55;
    const include3dFigure = !player.isSpecialDefender;
    const numberBadgeOffsetY = player.isSpecialDefender ? 0 : 14;
    const numberBadgeRadius = player.isSpecialDefender ? 18 : 15;
    const heldBallOffsetX = 30;
    const heldBallOffsetY = -8;

    let badgeFontSize = 32;
    if (charCount === 2) {
        badgeFontSize = 26;
    } else if (charCount >= 3) {
        badgeFontSize = 22;
    }
    if (player.isSpecialDefender) {
        badgeFontSize = badgeFontSize * 0.7;
    }

    const ringRadius = 24;
    const armsOffsetY = 4;
    const armDistance = 36;
    const armLength = 26;
    const armHeight = 8;
    const armStrokeWidth = 7;
    const rotation = player.armRotation || 0;

    const rotationRad = ((rotation + 90) * Math.PI) / 180;
    const specialBallDistance = ringRadius + 18;
    const specialBallOffsetX = specialBallDistance * Math.cos(rotationRad);
    const specialBallOffsetY = specialBallDistance * Math.sin(rotationRad);

    return (
        <Fragment key={player.id}>
            {(include3dFigure || player.isSpecialDefender) && (
                <g transform={`translate(${player.x}, ${player.y})`}>
                    <g transform={`scale(${scaleFactor})`}>
                        <ellipse
                            cx={0}
                            cy={38}
                            rx={34}
                            ry={14}
                            fill="rgba(0, 0, 0, 0.25)"
                        />
                    </g>
                </g>
            )}

            {player.isSpecialDefender && (
                <g transform={`translate(${player.x}, ${player.y})`}>
                    <g transform={`scale(${scaleFactor})`}>
                        <circle
                            cx={0}
                            cy={0}
                            r={ringRadius}
                            fill={`url(#${gradientIds.body})`}
                            stroke={palette.outline}
                            strokeWidth={4}
                            filter={filterId ? `url(#${filterId})` : undefined}
                        />
                        <g transform={`translate(0, ${armsOffsetY}) rotate(${rotation})`}>
                            <g transform={`translate(${-armDistance}, 0) rotate(-30)`}>
                                <path
                                    d={`M -${armLength} 0 A ${armLength} ${armHeight} 0 0 1 ${armLength} 0`}
                                    fill="none"
                                    stroke={palette.shadow}
                                    strokeWidth={armStrokeWidth + 4}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    opacity={0.7}
                                />
                                <path
                                    d={`M -${armLength} 0 A ${armLength} ${armHeight} 0 0 1 ${armLength} 0`}
                                    fill="none"
                                    stroke={`url(#${gradientIds.limb})`}
                                    strokeWidth={armStrokeWidth - 1}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d={`M -${armLength} 0 A ${armLength} ${armHeight} 0 0 1 ${armLength} 0`}
                                    fill="none"
                                    stroke={palette.highlight}
                                    strokeWidth={armStrokeWidth - 3}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    opacity={0.8}
                                />
                            </g>
                            <g transform={`translate(${armDistance}, 0) rotate(30)`}>
                                <path
                                    d={`M -${armLength} 0 A ${armLength} ${armHeight} 0 0 1 ${armLength} 0`}
                                    fill="none"
                                    stroke={palette.shadow}
                                    strokeWidth={armStrokeWidth + 4}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    opacity={0.7}
                                />
                                <path
                                    d={`M -${armLength} 0 A ${armLength} ${armHeight} 0 0 1 ${armLength} 0`}
                                    fill="none"
                                    stroke={`url(#${gradientIds.limb})`}
                                    strokeWidth={armStrokeWidth - 1}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d={`M -${armLength} 0 A ${armLength} ${armHeight} 0 0 1 ${armLength} 0`}
                                    fill="none"
                                    stroke={palette.highlight}
                                    strokeWidth={armStrokeWidth - 3}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    opacity={0.8}
                                />
                            </g>
                        </g>
                    </g>
                </g>
            )}

            {include3dFigure && (
                <g transform={`translate(${player.x}, ${player.y})`}>
                    <g transform={`scale(${scaleFactor})`}>
                        <g filter={filterId ? `url(#${filterId})` : undefined}>
                            <circle
                                cx={0}
                                cy={-28}
                                r={14}
                                fill={`url(#${gradientIds.head})`}
                                stroke={palette.outline}
                                strokeWidth={2.5}
                            />
                            <rect
                                x={-4}
                                y={-16}
                                width={8}
                                height={9}
                                rx={3}
                                fill={palette.mid}
                                stroke={palette.outline}
                                strokeWidth={2}
                            />
                            <path
                                d="M -22 -4 Q 0 -16 22 -4 L 20 36 Q 0 56 -20 36 Z"
                                fill={`url(#${gradientIds.body})`}
                                stroke={palette.outline}
                                strokeWidth={2.5}
                                strokeLinejoin="round"
                            />
                            <path
                                d="M -18 34 L -18 50 L -8 50 L -8 42 Q 0 48 8 42 L 8 50 L 18 50 L 18 34 Q 0 54 -18 34 Z"
                                fill={palette.shadow}
                                stroke={palette.outline}
                                strokeWidth={2.5}
                                strokeLinejoin="round"
                            />
                            <path
                                d="M -16 34 Q 0 42 16 34"
                                fill="none"
                                stroke={adjustColor(palette.mid, -0.2)}
                                strokeWidth={2.5}
                                strokeLinecap="round"
                            />
                            <rect
                                x={-20}
                                y={50}
                                width={14}
                                height={9}
                                rx={4}
                                fill={palette.limbSecondary}
                                stroke={palette.outline}
                                strokeWidth={2}
                            />
                            <rect
                                x={6}
                                y={50}
                                width={14}
                                height={9}
                                rx={4}
                                fill={palette.limbSecondary}
                                stroke={palette.outline}
                                strokeWidth={2}
                            />
                        </g>
                    </g>
                </g>
            )}

            {(include3dFigure || player.isSpecialDefender) && player.hasBall && (
                <g transform={`translate(${player.x}, ${player.y})`}>
                    <g transform={`scale(${scaleFactor})`}>
                        <g transform={`translate(${player.isSpecialDefender ? specialBallOffsetX : heldBallOffsetX}, ${player.isSpecialDefender ? specialBallOffsetY : heldBallOffsetY})`}>
                            <circle
                                r={14}
                                fill={heldBallGradientId ? `url(#${heldBallGradientId})` : "#F07A1C"}
                                stroke="#9E3F10"
                                strokeWidth={2.5}
                            />
                            <path d="M -14 0 L 14 0" stroke="#9E3F10" strokeWidth={1.6} fill="none" strokeLinecap="round" />
                            <path d="M 0 -14 A 14 14 0 0 1 0 14" stroke="#9E3F10" strokeWidth={1.6} fill="none" strokeLinecap="round" />
                        </g>
                    </g>
                </g>
            )}

            <g transform={`translate(${player.x}, ${player.y})`}>
                <g transform={`scale(${scaleFactor})`}>
                    <circle
                        cx={0}
                        cy={numberBadgeOffsetY}
                        r={numberBadgeRadius}
                        fill="#FFFFFF"
                        stroke="#FFFFFF"
                        strokeWidth={4}
                    />
                    <text
                        x={0}
                        y={numberBadgeOffsetY}
                        textAnchor="middle"
                        alignmentBaseline="middle"
                        fontFamily="Roboto, sans-serif"
                        fontSize={badgeFontSize}
                        fontWeight="bold"
                        fill={palette.mid}
                    >
                        {displayNumber}
                    </text>
                </g>
            </g>
        </Fragment>
    );
};

// Function to render a single line as an SVG group
export const renderLineSVG = (
    line: Line,
    players: Player[],
    allLines: Line[],
    playNid?: number,
    frameId?: number,
    options: {
        strokeWidth?: number;
        markerSize?: number;
        isForPDF?: boolean;
        simplifyZigzag?: boolean;
    } = {}
) => {
    const config = options.isForPDF ? FRAME_RENDER_CONFIG_FOR_PDF : FRAME_RENDER_CONFIG;
    const {
        strokeWidth = config.line.strokeWidth,
        markerSize = config.line.markerSize,
        isForPDF = false,
        simplifyZigzag = false,
    } = options;

    const fromPlayer = players.find((p) => p.id === line.fromId);
    if (!fromPlayer) return null;

    const isDefenderLine = fromPlayer.isDefender || false;
    const lineColor = line.color || (isDefenderLine ? "red" : "black");

    const adjustedFrom = { x: line.fromX, y: line.fromY };
    const adjustedTo = { x: line.toX, y: line.toY };
    const pathD = generatePathD(line, adjustedFrom, adjustedTo, allLines, isForPDF, 1.0, simplifyZigzag);

    // Build the same prefix as in SvgDefs
    const prefixParts: string[] = [];
    if (playNid !== undefined) {
        prefixParts.push(String(playNid));
    }
    if (frameId !== undefined) {
        prefixParts.push(String(frameId));
    }
    const prefix = prefixParts.join('-');

    // Clean colors for use in IDs (same as DialogPlayBuilder)
    const cleanedArrowColor = (line.type === 'handoff' ? 'rgba(255, 255, 255, 0.9)' : lineColor).replace(/[^a-z0-9]/gi, '_');
    const cleanedColor = lineColor.replace(/[^a-z0-9]/gi, '_');

    const arrowheadId = prefix ? `arrowhead-${prefix}-${line.id}-${cleanedArrowColor}` : `arrowhead-${line.id}-${cleanedArrowColor}`;
    const barEndId = prefix ? `barEnd-${prefix}-${line.id}-${cleanedColor}` : `barEnd-${line.id}-${cleanedColor}`;

    return (
        <g key={`line-${frameId || 'no-frame'}-${line.id}-${lineColor}`}>
            <SvgDefs
                key={`defs-${frameId || 'no-frame'}-${line.id}-${lineColor}`}
                lineId={line.id}
                color={lineColor}
                playNid={playNid}
                frameId={frameId}
                markerSize={markerSize}
                lineType={line.type}
            />
            {line.type !== "handoff" && (
                <path
                    d={pathD}
                    stroke={lineColor}
                    strokeWidth={strokeWidth}
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeDasharray={line.type === "dashed" || line.type === "shot" ? "5,5" : undefined}
                    markerEnd={
                        line.type === "bar"
                            ? `url(#${barEndId})`
                            : line.type === "shot"
                                ? undefined
                                : `url(#${arrowheadId})`
                    }
                />
            )}
            {line.type === "shot" && (
                <circle
                    cx={line.toX}
                    cy={line.toY}
                    r={8}
                    fill="none"
                    stroke={lineColor}
                    strokeWidth={2}
                />
            )}
            {line.type === "handoff" && (
                <>
                    {/* Calculate the midpoint of the handoff line */}
                    {(() => {
                        const midX = (adjustedFrom.x + adjustedTo.x) / 2;
                        const midY = (adjustedFrom.y + adjustedTo.y) / 2;
                        // Fixed orientation for handoff lines (1 horizontal line with 2 vertical lines at 20% and 80%)
                        const horizontalLineLength = 20; // Length of horizontal line
                        const verticalLineLength = 20; // Length of vertical lines

                        // Horizontal line (always horizontal)
                        const horizontalStartX = midX - horizontalLineLength / 2;
                        const horizontalStartY = midY;
                        const horizontalEndX = midX + horizontalLineLength / 2;
                        const horizontalEndY = midY;

                        // First vertical line at 30% of horizontal line
                        const firstVerticalX = midX - horizontalLineLength / 2 + (horizontalLineLength * 0.3);
                        const line1StartX = firstVerticalX;
                        const line1StartY = midY - verticalLineLength / 2;
                        const line1EndX = firstVerticalX;
                        const line1EndY = midY + verticalLineLength / 2;

                        // Second vertical line at 70% of horizontal line
                        const secondVerticalX = midX - horizontalLineLength / 2 + (horizontalLineLength * 0.7);
                        const line2StartX = secondVerticalX;
                        const line2StartY = midY - verticalLineLength / 2;
                        const line2EndX = secondVerticalX;
                        const line2EndY = midY + verticalLineLength / 2;

                        return (
                            <>
                                {/* Invisible clickable area for handoff symbol */}
                                <rect
                                    x={horizontalStartX - 5}
                                    y={midY - verticalLineLength / 2 - 5}
                                    width={horizontalLineLength + 10}
                                    height={verticalLineLength + 10}
                                    fill="transparent"
                                    style={{ cursor: 'pointer' }}
                                />
                                <line
                                    x1={horizontalStartX}
                                    y1={horizontalStartY}
                                    x2={horizontalEndX}
                                    y2={horizontalEndY}
                                    stroke={line.handoffColor || "#000000"}
                                    strokeWidth={2}
                                    strokeLinecap="round"
                                />
                                <line
                                    x1={line1StartX}
                                    y1={line1StartY}
                                    x2={line1EndX}
                                    y2={line1EndY}
                                    stroke={line.handoffColor || "#000000"}
                                    strokeWidth={2}
                                    strokeLinecap="round"
                                />
                                <line
                                    x1={line2StartX}
                                    y1={line2StartY}
                                    x2={line2EndX}
                                    y2={line2EndY}
                                    stroke={line.handoffColor || "#000000"}
                                    strokeWidth={2}
                                    strokeLinecap="round"
                                />
                            </>
                        );
                    })()}
                </>
            )}
        </g>
    );
};

// Function to render a single cone as an SVG group
export const renderConeSVG = (
    cone: Cone,
    options: {
        scale?: number;
        isForPDF?: boolean;
        use3D?: boolean;
        gradientId?: string;
        outlineColor?: string;
        baseColor?: string;
    } = {}
) => {
    const config = options.isForPDF ? FRAME_RENDER_CONFIG_FOR_PDF : FRAME_RENDER_CONFIG;
    const { scale = config.cone.scale, use3D = false, gradientId, outlineColor, baseColor } = options;
    const baseSize = 24; // Base size from the example
    const scaledSize = baseSize * scale;
    const x = cone.x - scaledSize / 2;
    const y = cone.y - scaledSize / 2;
    const color = baseColor || cone.color || "orange";

    const bodyPath = `M${x + 6 * scale},${y + 15 * scale} 
                    L${x + 9 * scale},${y + 2 * scale} 
                    Q${x + 10 * scale},${y + 1 * scale},${x + 11 * scale},${y + 2 * scale} 
                    L${x + 14 * scale},${y + 15 * scale} 
                    L${x + 6 * scale},${y + 15 * scale}Z 
                    M${x + 4 * scale},${y + 15 * scale} 
                    Q${x + 3 * scale},${y + 15 * scale},${x + 3 * scale},${y + 15.5 * scale} 
                    L${x + 3 * scale},${y + 16 * scale} 
                    L${x + 17 * scale},${y + 16 * scale} 
                    L${x + 17 * scale},${y + 15.5 * scale} 
                    Q${x + 17 * scale},${y + 15 * scale},${x + 16 * scale},${y + 15 * scale}`;

    if (use3D && gradientId) {
        return (
            <g key={cone.id} id={`cone-${cone.id}`}>
                <path
                    d={bodyPath}
                    fill={`url(#${gradientId})`}
                    stroke={outlineColor || adjustColor(color, -0.35)}
                    strokeWidth={1.2 * scale}
                    strokeLinejoin="round"
                />
            </g>
        );
    }

    return (
        <g key={cone.id} id={`cone-${cone.id}`}>
            <path
                d={bodyPath}
                fill={color}
            />
        </g>
    );
};

interface Frame {
    id: number;
    players: Player[];
    lines: Line[];
    cones: Cone[];
    texts?: TextBox[];
    shades?: Shade[];
}

const estimateTextWidthPx = (text: string, fontSize: number) => {
    // Rough but better-than-char-count estimator for Roboto/Arial bold.
    // Wide chars take more space, narrow chars less.
    const wide = "WM@#%&";
    const narrow = "ilI1.,:;|!'`";
    let total = 0;
    for (const ch of text) {
        if (ch === " ") total += fontSize * 0.33;
        else if (wide.includes(ch)) total += fontSize * 0.92;
        else if (narrow.includes(ch)) total += fontSize * 0.32;
        else total += fontSize * 0.62;
    }
    return total;
};

const wrapTextLinesByWidth = (text: string, maxWidthPx: number, fontSize: number) => {
    if (!text) return [""];
    const paragraphs = text.split(/\r?\n/);
    const lines: string[] = [];
    const maxW = Math.max(1, maxWidthPx);

    const breakLongWord = (word: string) => {
        let chunk = "";
        for (const ch of word) {
            const cand = chunk + ch;
            if (estimateTextWidthPx(cand, fontSize) <= maxW) {
                chunk = cand;
            } else {
                if (chunk) lines.push(chunk);
                chunk = ch;
            }
        }
        if (chunk) lines.push(chunk);
    };

    for (const para of paragraphs) {
        const words = para.split(/\s+/).filter(Boolean);
        if (words.length === 0) {
            lines.push("");
            continue;
        }
        let current = "";
        for (const w of words) {
            const candidate = current ? `${current} ${w}` : w;
            if (estimateTextWidthPx(candidate, fontSize) <= maxW) {
                current = candidate;
                continue;
            }
            if (current) lines.push(current);
            // If the single word doesn't fit, break it
            if (estimateTextWidthPx(w, fontSize) > maxW) {
                breakLongWord(w);
                current = "";
            } else {
                current = w;
            }
        }
        if (current) lines.push(current);
    }
    return lines.length ? lines : [""];
};

const renderTextBoxSVG = (tb: TextBox, options?: { isForPDF?: boolean }) => {
    const padding = 6;
    const fontSize = tb.fontSize ?? (options?.isForPDF ? 18 : 14);
    const lineHeight = fontSize * 1.2;
    const innerW = Math.max(1, tb.width - padding * 2);
    const innerH = Math.max(1, tb.height - padding * 2);
    const maxLines = Math.max(1, Math.floor(innerH / lineHeight));
    const rawLines = wrapTextLinesByWidth(tb.text || "", innerW, fontSize);
    const lines = rawLines.slice(0, maxLines);

    const textX = tb.x + padding;
    const textY = tb.y + padding + fontSize;

    return (
        <g key={`textbox-${tb.id}`} id={`textbox-${tb.id}`}>
            <text
                x={textX}
                y={textY}
                fontSize={fontSize}
                fill={"#111111"}
                fontWeight="normal"
                fontFamily="Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"
            >
                {lines.map((ln, idx) => (
                    <tspan key={`tb-${tb.id}-ln-${idx}`} x={textX} dy={idx === 0 ? 0 : lineHeight}>
                        {ln}
                    </tspan>
                ))}
            </text>
        </g>
    );
};

const renderShadeSVG = (sh: Shade) => {
    const FILL = toRgba((sh as any).color, 0.24);
    if (sh.shape === "rect") {
        return (
            <rect
                key={`shade-${sh.id}`}
                x={sh.x}
                y={sh.y}
                width={sh.width}
                height={sh.height}
                rx={10}
                ry={10}
                fill={FILL}
            />
        );
    }
    return (
        <ellipse
            key={`shade-${sh.id}`}
            cx={sh.x + sh.width / 2}
            cy={sh.y + sh.height / 2}
            rx={Math.max(1, sh.width / 2)}
            ry={Math.max(1, sh.height / 2)}
            fill={FILL}
        />
    );
};

export const renderFrameSVG = (
    frame: Frame,
    courtType: "halfcourt" | "fullcourt",
    playNid?: number,
    options: {
        playerFontSize?: number;
        playerBallRadius?: number;
        playerBallStrokeWidth?: number;
        lineStrokeWidth?: number;
        lineMarkerSize?: number;
        coneScale?: number;
        courtRenderMode?: 'whiteBackground' | 'woodBackground';
        hideLogo?: boolean;
        hideBorders?: boolean;
        isForPDF?: boolean;
        use3DPlayers?: boolean;
        allow3DForPDF?: boolean;
        simplifyZigzag?: boolean;
        courtColors?: {
            lineColor?: string;
            paintAreaColor?: string | null;
            paintAreaUseWoodPattern?: boolean;
            midRangeColor?: string | null;
            midRangeUseWoodPattern?: boolean;
            outOfBoundsColor?: string | null;
            outOfBoundsUseWoodPattern?: boolean;
        };
    } = {}
) => {
    const {
        playerFontSize,
        playerBallRadius,
        playerBallStrokeWidth,
        lineStrokeWidth,
        lineMarkerSize,
        coneScale,
        courtRenderMode = 'whiteBackground', // Default to static images
        hideLogo = false,
        hideBorders = false,
        isForPDF = false,
        use3DPlayers = true,
        allow3DForPDF = false,
        courtColors,
        simplifyZigzag = false,
    } = options;

    const courtWidth = courtDimensions[courtType].width;
    const courtHeight = courtDimensions[courtType].height;

    const shouldUse3DPlayers = use3DPlayers && (!isForPDF || allow3DForPDF);

    const prefixParts: string[] = [];
    if (playNid !== undefined) {
        prefixParts.push(String(playNid));
    }
    if (frame.id !== undefined && frame.id !== null) {
        prefixParts.push(String(frame.id));
    }
    const framePrefix = prefixParts.length ? prefixParts.join("-") : "frame";
    const filterId = shouldUse3DPlayers ? `player-soft-shadow-${framePrefix}` : undefined;
    const heldBallGradientId = shouldUse3DPlayers ? `basketball-held-gradient-${framePrefix}` : undefined;

    let playerMeta: Record<number, { palette: GradientPalette; gradientIds: { head: string; body: string; limb: string } }> = {};
    if (shouldUse3DPlayers) {
        playerMeta = frame.players.reduce<Record<number, { palette: GradientPalette; gradientIds: { head: string; body: string; limb: string } }>>((acc, player) => {
            const baseColor = player.color || (player.isDefender ? "red" : "black");
            const palette = getGradientPalette(baseColor);
            const gradientBaseId = `player-gradient-${framePrefix}-${player.id}`;
            acc[player.id] = {
                palette,
                gradientIds: {
                    head: `${gradientBaseId}-head`,
                    body: `${gradientBaseId}-body`,
                    limb: `${gradientBaseId}-limb`,
                },
            };
            return acc;
        }, {});
    }

    const coneMeta: Record<number, { gradientId: string; outline: string; baseColor: string }> = {};
    const coneColorMeta: Record<string, { gradientId: string; palette: GradientPalette }> = {};
    if (shouldUse3DPlayers && frame.cones && frame.cones.length > 0) {
        (frame.cones || []).forEach((cone) => {
            const colorKey = cone.color || "#FFA500";
            if (!coneColorMeta[colorKey]) {
                const palette = getGradientPalette(colorKey);
                const gradientId = `cone-gradient-${framePrefix}-${Object.keys(coneColorMeta).length}`;
                coneColorMeta[colorKey] = { gradientId, palette };
            }
        });
        (frame.cones || []).forEach((cone) => {
            const colorKey = cone.color || "#FFA500";
            const meta = coneColorMeta[colorKey];
            coneMeta[cone.id] = {
                gradientId: meta.gradientId,
                outline: meta.palette.outline,
                baseColor: colorKey,
            };
        });
    }

    return (
        <svg
            width={courtWidth}
            height={courtHeight}
            viewBox={`0 0 ${courtWidth} ${courtHeight}`}
            preserveAspectRatio="xMidYMid meet"
        >
            <defs>
                {shouldUse3DPlayers && filterId && (
                    <>
                        <filter id={filterId} x="-50%" y="-50%" width="200%" height="200%">
                            <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000000" floodOpacity="0.35" />
                        </filter>
                        {heldBallGradientId && (
                            <radialGradient id={heldBallGradientId} cx="50%" cy="35%" r="70%">
                                <stop offset="0%" stopColor="#FFE6B3" />
                                <stop offset="55%" stopColor="#F4831F" />
                                <stop offset="100%" stopColor="#B54A12" />
                            </radialGradient>
                        )}
                        {frame.players.map((player) => {
                            const meta = playerMeta[player.id];
                            if (!meta) return null;
                            const { palette, gradientIds } = meta;
                            return (
                                <Fragment key={`gradient-${player.id}`}>
                                    <radialGradient id={gradientIds.head} cx="50%" cy="30%" r="70%">
                                        <stop offset="0%" stopColor={palette.highlight} />
                                        <stop offset="60%" stopColor={palette.mid} />
                                        <stop offset="100%" stopColor={palette.shadow} />
                                    </radialGradient>
                                    <linearGradient id={gradientIds.body} x1="0%" y1="0%" x2="0%" y2="100%">
                                        <stop offset="0%" stopColor={palette.highlight} />
                                        <stop offset="55%" stopColor={palette.mid} />
                                        <stop offset="100%" stopColor={palette.shadow} />
                                    </linearGradient>
                                    <linearGradient id={gradientIds.limb} x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop offset="0%" stopColor={palette.limbPrimary} />
                                        <stop offset="100%" stopColor={palette.limbSecondary} />
                                    </linearGradient>
                                </Fragment>
                            );
                        })}
                        {(shouldUse3DPlayers && Object.keys(coneColorMeta).length > 0) &&
                            Object.values(coneColorMeta).map(({ gradientId, palette }) => {
                                const topColor = adjustColor(palette.highlight, 0.15);
                                return (
                                    <linearGradient key={gradientId} id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
                                        <stop offset="0%" stopColor={topColor} />
                                        <stop offset="55%" stopColor={palette.mid} />
                                        <stop offset="100%" stopColor={palette.shadow} />
                                    </linearGradient>
                                );
                            })}
                    </>
                )}
            </defs>

            {/* Background court */}
            {
                courtRenderMode === 'whiteBackground' ?
                    <image
                        xlinkHref={
                            isForPDF
                                ? (courtType === "halfcourt" ? halfcourtIcon : fullcourtIcon)
                                : (courtType === "halfcourt" ? "/images/halfcourt.svg" : "/images/fullcourt.svg")
                        }
                        x="0"
                        y="0"
                        width={courtWidth}
                        height={courtHeight}
                        preserveAspectRatio="none"
                    />
                    : <Court courtType={courtType} {...courtColors} hideLogo={hideLogo} hideBorders={hideBorders} />
            }

            {/* Render shades (under everything) */}
            {(frame.shades || []).map((sh) => renderShadeSVG(sh))}

            {/* Render text boxes (annotations) */}
            {(frame.texts || []).map((tb) => renderTextBoxSVG(tb, { isForPDF }))}

            {/* Render cones */}
            {(frame.cones || []).map((cone) =>
                renderConeSVG(cone, {
                    scale: coneScale,
                    isForPDF,
                    use3D: shouldUse3DPlayers,
                    gradientId: coneMeta[cone.id]?.gradientId,
                    outlineColor: coneMeta[cone.id]?.outline,
                    baseColor: coneMeta[cone.id]?.baseColor,
                })
            )}

            {/* Render lines */}
            {frame.lines.map((line) =>
                renderLineSVG(line, frame.players, frame.lines, playNid, frame.id, {
                    strokeWidth: lineStrokeWidth,
                    markerSize: lineMarkerSize,
                    isForPDF,
                    simplifyZigzag,
                })
            )}

            {/* Render players */}
            {frame.players.map((player) =>
                renderPlayerSVG(player, {
                    fontSize: playerFontSize,
                    ballRadius: playerBallRadius,
                    ballStrokeWidth: playerBallStrokeWidth,
                    isForPDF,
                    allow3DForPDF: allow3DForPDF,
                    gradientIds: shouldUse3DPlayers ? playerMeta[player.id]?.gradientIds : undefined,
                    palette: shouldUse3DPlayers ? playerMeta[player.id]?.palette : undefined,
                    filterId,
                    heldBallGradientId,
                })
            )}
        </svg>
    );
};